import { useMutation, useQueryClient } from "react-query";
import { useDirections } from "./useDirections";
import { DirectionsPutSchema } from "../model/shema";

interface MutateOptions {
  onSuccess?: (data?: any) => void;
  onError?: (error: any) => void;
}

interface DataIProps {
  data: DirectionsPutSchema;
  options?: MutateOptions;
}

export const useUpdateDirection = (queryKey: string | any[] = "directions") => {
  const { directionsPut } = useDirections();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: ["updateDirection", queryKey],
    mutationFn: async (props: DataIProps) => {
      const result = await directionsPut(props.data);
      if (result.data) {
        return result.data;
      } else {
        throw new Error("Не удалось обновить направление");
      }
    },
    onMutate: async (variables: DataIProps) => {
      const { data } = variables;
      await queryClient.cancelQueries(queryKey);
      const previousData = queryClient.getQueryData(queryKey);
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return data;

        if (Array.isArray(old)) {
          return old.map((item) => {
            if (item.instanceId === data.instanceId) {
              return { ...item, ...data };
            }
            return item;
          });
        }

        return { ...old, ...data };
      });

      return { previousData };
    },
    onError: (err: any, variables: DataIProps, context: any) => {
      queryClient.setQueryData(queryKey, context.previousData);
      variables.options?.onError?.(err);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(queryKey, (old: any) => {
        if (!old) return data;

        if (Array.isArray(old)) {
          return old.map((item) => {
            if (item.instanceId === data[`instanceId`]) {
              return data;
            }
            return item;
          });
        }

        return data;
      });
      variables.options?.onSuccess?.(data);
    },
    onSettled: () => {
      queryClient.invalidateQueries(queryKey);
    },
  });

  const update = (data: DirectionsPutSchema, options?: MutateOptions) => {
    mutation.mutate({ data, options });
  };

  return { update, isLoading: mutation.isLoading, isError: mutation.isError };
};
